/**
 * ラリーのスポットを地図に出すためのデータ取得と絞り込み
 *
 * 同じ場所が複数のラリーに登録されているとピンが重なって下のピンを押せないため、場所単位でまとめる。
 */

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getPlanPlaceUsages } from '@/api/services/route-plan'
import { createStamp, deleteStamp, getRallyMapItems } from '@/api/services/stamp-rally'
import type { PlanRef } from '@/api/types/route-plan'
import type { RallyMapItem, StampRallySummary } from '@/api/types/stamp-rally'

import type { RallyPlaceGroup } from './rally-place-popup'
import { matchesStampFilter, type StampFilter } from './stamp-filter'

export const RALLY_FALLBACK_COLOR = '#F97316'

/** 場所ごとにまとめたピン。shown は絞り込みを通ったラリーの分だけ */
export type VisiblePlace = {
  place: RallyPlaceGroup
  shown: RallyMapItem[]
}

/** 絞り込みバーに出すラリー 1 件。プラン側はスポットの紐づきから組み立てるので、必要な分だけ持つ */
export type RallyLegendEntry = {
  rally: { id: number; title: string; color: string | null }
  count: number
}

/** 絞り込みバーに出すプラン 1 件。count はいま地図に出せる場所のうち、そのプランに入っている数 */
export type PlanLegendEntry = {
  plan: PlanRef
  count: number
}

type Options = {
  /** 凡例と色に使う */
  rallies: StampRallySummary[]
  /** 地図に出さない場所。すでにプランへ入っているスポットなど */
  excludePlaceIds?: number[]
  /** false のあいだは取得しない。追加モードに入るまで待たせる用 */
  enabled?: boolean
  /** 指定するとそのラリーのスポットだけに絞る */
  focusRallyId?: number | null
  /** ポップアップを開いている場所。絞り込みから外れても消さない */
  keepPlaceId?: number | null
  /** いま編集しているプラン。自分自身は「他のプランに追加済み」に数えない */
  currentPlanId?: number | null
  /** 追加の絞り込み。キーワードや県で地図も一緒に絞るときに渡す（useCallback 必須） */
  filterItem?: (item: RallyMapItem) => boolean
  /** filterItem の中身を表すキー。変わると表示範囲を取り直す。1 文字ごとに動かしたくない絞り込みでは渡さない */
  filterKey?: string
}

export const useRallyPlaces = ({
  rallies,
  excludePlaceIds,
  enabled = true,
  focusRallyId = null,
  keepPlaceId = null,
  currentPlanId = null,
  filterItem,
  filterKey = '',
}: Options) => {
  const [items, setItems] = useState<RallyMapItem[]>([])
  const [planRefs, setPlanRefs] = useState<Map<number, PlanRef[]>>(new Map())
  const [isLoading, setIsLoading] = useState(enabled)
  const [stampFilter, setStampFilter] = useState<StampFilter>('unstamped')
  const [hiddenRallyIds, setHiddenRallyIds] = useState<number[]>([])
  const [hiddenPlanIds, setHiddenPlanIds] = useState<number[]>([])
  // スポットの追加や編集のあとに引き直すための合図
  const [reloadKey, setReloadKey] = useState(0)

  // 未獲得の絞り込みはクライアント側で行う。ポップアップでは獲得済みのラリーも並べたいので、
  // 取得の時点で間引いてしまうと同じ場所の他ラリーが見えなくなる
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    setIsLoading(true)
    getRallyMapItems(undefined, controller.signal)
      .then(setItems)
      .catch(() => {
        if (!controller.signal.aborted) toast.error('スポットの取得に失敗しました')
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [enabled, reloadKey])

  // 追加済みの目印。件数はプランのスポット数どまりなので、地図と同時に 1 回だけ引く
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    getPlanPlaceUsages(controller.signal)
      .then((usages) => setPlanRefs(new Map(usages.map((usage) => [usage.place_id, usage.plans]))))
      .catch(() => {
        // 追加済みが分からなくても地図自体は使えるので、失敗しても黙って通す
      })
    return () => controller.abort()
  }, [enabled])

  const colorOf = useMemo(() => {
    const map = new Map(rallies.map((rally) => [rally.id, rally.color ?? RALLY_FALLBACK_COLOR]))
    return (item: RallyMapItem) => item.rally_color ?? map.get(item.rally_id) ?? RALLY_FALLBACK_COLOR
  }, [rallies])

  const places = useMemo<RallyPlaceGroup[]>(() => {
    const byPlace = new Map<number, RallyMapItem[]>()
    for (const item of items) {
      if (excludePlaceIds?.includes(item.place_id)) continue
      byPlace.set(item.place_id, [...(byPlace.get(item.place_id) ?? []), item])
    }
    return [...byPlace.values()].map((group) => ({
      placeId: group[0].place_id,
      name: group[0].name,
      address: group[0].address,
      position: { lat: group[0].latitude ?? 0, lng: group[0].longitude ?? 0 },
      items: group,
      plans: (planRefs.get(group[0].place_id) ?? []).filter((plan) => plan.id !== currentPlanId),
    }))
  }, [items, excludePlaceIds, planRefs, currentPlanId])

  const visible = useMemo<VisiblePlace[]>(
    () =>
      places.flatMap((place) => {
        // 追加済みを隠す指定は場所丸ごと消す。複数のプランに入っているなら、隠す側を優先する
        const hiddenByPlan = place.plans.some((plan) => hiddenPlanIds.includes(plan.id))
        const shown = hiddenByPlan
          ? []
          : place.items.filter(
              (item) =>
                (focusRallyId === null || item.rally_id === focusRallyId) &&
                !hiddenRallyIds.includes(item.rally_id) &&
                matchesStampFilter(item.stamped_at, stampFilter) &&
                (!filterItem || filterItem(item)),
            )
        if (shown.length > 0) return [{ place, shown }]
        // スタンプを押した拍子にポップアップごと消えると、押せたのか分からない
        return place.placeId === keepPlaceId ? [{ place, shown: place.items }] : []
      }),
    [places, hiddenRallyIds, hiddenPlanIds, stampFilter, focusRallyId, keepPlaceId, filterItem],
  )

  const points = useMemo(() => visible.map(({ place }) => place.position), [visible])

  // 表示中のラリーだけを凡例に出す。0 件のラリーを並べても選びようがない
  // 非表示のラリーは戻せるように残すので、絞り込みは獲得状況だけを見る
  const legend = useMemo<RallyLegendEntry[]>(() => {
    const counts = new Map<number, number>()
    for (const place of places) {
      for (const item of place.items) {
        if (!matchesStampFilter(item.stamped_at, stampFilter)) continue
        counts.set(item.rally_id, (counts.get(item.rally_id) ?? 0) + 1)
      }
    }
    return rallies.filter((rally) => counts.has(rally.id)).map((rally) => ({ rally, count: counts.get(rally.id) ?? 0 }))
  }, [places, rallies, stampFilter])

  // バッジに出すのは、いま地図に出せる場所を含むプランだけ。関係のないプランを並べても押しようがない
  const planLegend = useMemo<PlanLegendEntry[]>(() => {
    const counts = new Map<number, number>()
    const byId = new Map<number, PlanRef>()
    for (const place of places) {
      if (place.items.every((item) => !matchesStampFilter(item.stamped_at, stampFilter))) continue
      for (const plan of place.plans) {
        byId.set(plan.id, plan)
        counts.set(plan.id, (counts.get(plan.id) ?? 0) + 1)
      }
    }
    return [...byId.values()].map((plan) => ({ plan, count: counts.get(plan.id) ?? 0 }))
  }, [places, stampFilter])

  const toggleRally = (rallyId: number) =>
    setHiddenRallyIds((prev) => (prev.includes(rallyId) ? prev.filter((id) => id !== rallyId) : [...prev, rallyId]))

  const setAllRalliesHidden = (hidden: boolean) => setHiddenRallyIds(hidden ? legend.map(({ rally }) => rally.id) : [])

  const togglePlan = (planId: number) =>
    setHiddenPlanIds((prev) => (prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]))

  const setAllPlansHidden = (hidden: boolean) => setHiddenPlanIds(hidden ? planLegend.map(({ plan }) => plan.id) : [])

  // 絞り込みで押した場所がその場で消えることがあるので、何が起きたかはトーストで伝える
  const toggleStamp = async (item: RallyMapItem, stamped: boolean) => {
    const rollback = items
    setItems((current) =>
      current.map((row) =>
        row.id === item.id ? { ...row, stamped_at: stamped ? new Date().toISOString() : null } : row,
      ),
    )
    try {
      const updated = stamped ? await createStamp(item.id) : await deleteStamp(item.id)
      setItems((current) => current.map((row) => (row.id === item.id ? { ...row, ...updated } : row)))
      toast.success(stamped ? `${item.rally_title} のスタンプを獲得しました` : 'スタンプを取り消しました')
    } catch {
      setItems(rollback)
      toast.error('スタンプの更新に失敗しました')
    }
  }

  return {
    isLoading,
    hasItems: items.length > 0,
    refetch: () => setReloadKey((prev) => prev + 1),
    visible,
    points,
    legend,
    planLegend,
    stampFilter,
    setStampFilter,
    hiddenRallyIds,
    toggleRally,
    setAllRalliesHidden,
    hiddenPlanIds,
    togglePlan,
    setAllPlansHidden,
    toggleStamp,
    colorOf,
    fitKey: `${stampFilter}-${focusRallyId ?? 'all'}-${hiddenRallyIds.join(',')}-${hiddenPlanIds.join(',')}-${filterKey}`,
  }
}

export type RallyPlacesResult = ReturnType<typeof useRallyPlaces>
